import { useState, useEffect } from 'react';
import { X, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getAuth } from 'firebase/auth';

const formatRoute = (trip, t) => {
  const originCity = trip.originCity || trip.originDetails?.city;
  const originCountry = trip.originCountry || trip.originDetails?.country;
  const destinationCity = trip.destinationCity || trip.destinationDetails?.city;
  const destinationCountry =
    trip.destinationCountry || trip.destinationDetails?.country;

  return `${originCity || t('home.from')}, ${originCountry || ''} → ${
    destinationCity || t('home.to')
  }, ${destinationCountry || ''}`;
};

const formatDeparture = (trip, t) => {
  if (!trip.departureDate) return t('tripCard.scheduleTbd');
  if (trip.departureTime) {
    return `${trip.departureDate} • ${trip.departureTime}`;
  }
  return trip.departureDate;
};

const formatPrice = (trip, t) => {
  const currency = trip.currency || 'USD';
  if (trip.pricingMode === 'perKg' && trip.pricePerKg) {
    return `${currency} ${trip.pricePerKg}/${trip.spaceUnit || 'kg'}`;
  }
  if (trip.pricingMode === 'perUnit' && trip.pricePerUnit) {
    return `${currency} ${trip.pricePerUnit}/${trip.spaceUnit || 'unit'}`;
  }
  if (trip.pricingMode === 'flat' && trip.flatPrice) {
    return `${currency} ${trip.flatPrice} flat`;
  }
  if (trip.flatPrice) {
    return `${currency} ${trip.flatPrice}`;
  }
  return t('tripCard.contactForPrice');
};

export function RequestModal({ visible, onClose, trip }) {
  const { user } = usePublicAuth();
  const [formData, setFormData] = useState({
    itemTitle: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const { t } = useTranslation();

  // Load wallet balance when modal opens
  useEffect(() => {
    if (visible && user && trip?.tripType === 'shipping') {
      loadBalance();
    }
  }, [visible, user, trip]);

  const loadBalance = async () => {
    if (!user) return null;
    setLoadingBalance(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const balance = Number(userData?.walletBalance || 0);
        setWalletBalance(balance);
        return balance;
      }
    } catch (error) {
      console.error('Error loading balance:', error);
    } finally {
      setLoadingBalance(false);
    }
    return walletBalance;
  };

  if (!visible || !trip) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.itemTitle.trim()) {
      alert(t('requestModal.validation.itemNameRequired'));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validateForm()) return;

    if (!user) {
      alert(t('requestModal.validation.loginRequired'));
      return;
    }

    setIsSubmitting(true);

    // Check balance for shipping trips
    const tripData = trip;
    const requestPrice = parseFloat(
      tripData.flatPrice || tripData.pricePerUnit || 0,
    );
    const fee = requestPrice * 0.15;
    const total = requestPrice + fee;

    if (tripData.tripType === 'shipping') {
      // Refresh balance before checking
      const latestBalance = await loadBalance();
      const currentBalance =
        typeof latestBalance === 'number' ? latestBalance : walletBalance;

      if (currentBalance < total) {
        const shortfall = total - currentBalance;
        const currency = tripData.currency || 'USD';
        alert(
          t('requestModal.validation.insufficientBalance', {
            currency,
            current: currentBalance.toFixed(2),
            total: total.toFixed(2),
            shortfall: shortfall.toFixed(2),
          }),
        );
        setIsSubmitting(false);
        return; // Do not allow request submission with insufficient balance
      }
    }

    try {
      const tripId = trip.id;
      const tripOwnerId = trip.createdBy || trip.ownerId || trip.userId;

      if (!tripId) {
        alert(t('requestModal.error.tripIdMissing'));
        setIsSubmitting(false);
        return;
      }

      if (!tripOwnerId) {
        alert(t('requestModal.error.tripOwnerMissing'));
        setIsSubmitting(false);
        return;
      }

      const route = formatRoute(trip, t);
      const departure = formatDeparture(trip, t);

      const requestData = {
        tripId: tripId,
        tripOwnerId: tripOwnerId,
        requesterId: user.uid,
        requesterName:
          user.displayName ||
          user.email?.split('@')[0] ||
          t('common.unknownUser'),
        requesterEmail: user.email,

        // Add trip type to request
        tripType: trip.tripType || 'shipping',

        // Item details
        itemTitle: formData.itemTitle.trim(),
        itemDescription: formData.itemTitle.trim(),
        weight: parseFloat(trip.availableSpace) || 0,
        spaceUnit: trip.spaceUnit || 'kg',
        materialType: trip.materialType || 'general',
        notes: formData.notes.trim(),

        // Trip details (for reference)
        route: route,
        departure: departure,

        // Pricing - for shipping trips, use trip's pricing
        price: tripData.flatPrice || tripData.pricePerUnit || 0,
        estimatedPrice: tripData.flatPrice || tripData.pricePerUnit || 0,
        currency: tripData.currency || 'USD',

        // Delivery method
        deliveryMethod: tripData.deliveryMethod || 'byRoad',

        // Status
        status: 'pending',

        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'requests'), requestData);

      // Reset form
      setFormData({
        itemTitle: '',
        notes: '',
      });

      alert(t('requestModal.success'));
      onClose();
    } catch (error) {
      console.error('Error sending request:', error);
      alert(t('requestModal.error.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      itemTitle: '',
      notes: '',
    });
    onClose();
  };

  const requestPrice = parseFloat(trip.flatPrice || trip.pricePerUnit || 0);
  const fee = requestPrice * 0.15;
  const total = requestPrice + fee;
  const currency = trip.currency || 'USD';

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${
        visible ? 'block' : 'hidden'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-800">
          <h2 className="text-2xl font-black text-gray-900 dark:text-white">
            {t('requestModal.title')}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5 text-gray-600 dark:text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Traveler Info */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-800 rounded-xl p-4 mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {trip.createdByName || t('requestModal.travelerFallback')}
            </h3>
            <p className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1">
              {formatRoute(trip)}
            </p>
            <p className="text-sm text-gray-600 dark:text-slate-400">
              {formatDeparture(trip)}
            </p>
          </div>

          {/* Form */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                {t('requestModal.itemDetails')}
              </h3>

              {/* Item Name */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                  {t('requestModal.itemName')}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder={t('requestModal.itemNamePlaceholder')}
                  value={formData.itemTitle}
                  onChange={e => handleInputChange('itemTitle', e.target.value)}
                  maxLength={100}
                  className="w-full border-2 border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-yellow-500 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                />
              </div>

              {/* Notes */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                  {t('requestModal.notesLabel')}
                </label>
                <textarea
                  placeholder={t('requestModal.notesPlaceholder')}
                  value={formData.notes}
                  onChange={e => handleInputChange('notes', e.target.value)}
                  maxLength={500}
                  rows={4}
                  className="w-full border-2 border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-yellow-500 resize-none placeholder:text-gray-400 dark:placeholder:text-slate-500"
                />
              </div>
            </div>

            {/* Pricing Info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {t('requestModal.pricing')}
              </h3>
              <p className="text-xl font-black text-blue-900 dark:text-blue-300 mb-2">
                {formatPrice(trip)}
              </p>
              {requestPrice > 0 && (
                <div className="mt-3 space-y-1 text-sm">
                  <div className="flex justify-between text-gray-700 dark:text-slate-300">
                    <span>{t('requestModal.subtotal')}</span>
                    <span className="font-semibold">
                      {currency} {requestPrice.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-700 dark:text-slate-300">
                    <span>{t('requestModal.serviceFee')}</span>
                    <span className="font-semibold">
                      {currency} {fee.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-900 dark:text-white border-t border-blue-200 dark:border-blue-800 pt-2 mt-2">
                    <span className="font-bold">{t('requestModal.total')}</span>
                    <span className="font-black text-lg">
                      {currency} {total.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
              {loadingBalance ? (
                <p className="text-sm text-blue-700 dark:text-blue-400 mt-2">
                  {t('requestModal.loadingBalance')}
                </p>
              ) : (
                <p className="text-sm text-blue-700 dark:text-blue-400 mt-2">
                  {t('requestModal.balance', {
                    currency,
                    amount: walletBalance.toFixed(2),
                  })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-slate-800 p-6">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.itemTitle.trim()}
            className="w-full bg-black dark:bg-slate-700 text-yellow-400 rounded-xl px-6 py-4 text-center text-base font-bold hover:bg-gray-900 dark:hover:bg-slate-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-400"></div>
                <span>{t('requestModal.sending')}</span>
              </>
            ) : (
              <>
                <Send className="h-5 w-5" />
                <span>{t('requestModal.sendRequest')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
